"use client";

import React from "react";
import { MessageCircle, Loader, Check } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
export default function InitiatePilotProgram() {
  return <section data-section-id="66319" id="ai-strategy-session" className="bg-background text-foreground relative py-24 px-4 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-primary)_0%,transparent_70%)] opacity-10 pointer-events-none" />
      
      <div className="relative z-10 max-w-[800px] mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-default font-bold tracking-tight mb-4 text-foreground">
            Request Your Custom AI Audit
          </h2>
          <p className="text-foreground/70 text-lg font-default">
            Get a comprehensive analysis of how AI can transform your business operations today.
          </p>
        </div>

        <div className="relative group rounded-sm bg-background border border-primary/20 shadow-md p-6 md:p-10 transition-all duration-500 hover:shadow-lg">
          <FormProvider formKey="ai_strategy_session_form_home" formId="ai_strategy_session_form_home" sectionName="ai-strategy-session" defaultValues={{
          company_name: "",
          contact_name: "",
          email: "",
          phone: "",
          industry: [],
          audit_details: ""
        }} validationRules={{
          company_name: {
            required: true
          },
          contact_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          industry: {
            required: true
          },
          audit_details: {
            required: true,
            minLength: 50,
            maxLength: 1000
          }
        }} submitText="Request Your Custom AI Audit" successMessage="Audit request received. Our AI specialists will reach out shortly.">
            <PilotForm />
          </FormProvider>
        </div>
      </div>

      {}
      <div className="absolute bottom-6 right-6 z-20">
        <Button size="icon" className="h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 hover:scale-105 transition-all duration-300" aria-label="Live Chat">
          <MessageCircle className="size-6" />
        </Button>
      </div>
    </section>;
}
function PilotForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <Check className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Request Received</h3>
        <p className="text-foreground/70 font-default max-w-md">{successMessage}</p>
      </div>;
  }
  const industryOptions = ["E-commerce", "Healthcare", "Real Estate", "Manufacturing", "Professional Services"];
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-semibold">Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme AI Corp" {...field} className="bg-[#F0FAFF] border-primary/40 focus:border-primary focus:ring-[4px] focus:ring-[rgba(40,170,222,0.4)] text-foreground placeholder:text-foreground/40 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="contact_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-semibold">Contact Person</FormLabel>
              <FormControl>
                <Input placeholder="Alex Smith" {...field} className="bg-[#F0FAFF] border-primary/40 focus:border-primary focus:ring-[4px] focus:ring-[rgba(40,170,222,0.4)] text-foreground placeholder:text-foreground/40 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-semibold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="alex@zemio.com" {...field} className="bg-[#F0FAFF] border-primary/40 focus:border-primary focus:ring-[4px] focus:ring-[rgba(40,170,222,0.4)] text-foreground placeholder:text-foreground/40 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-semibold">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="bg-[#F0FAFF] border-primary/40 focus:border-primary focus:ring-[4px] focus:ring-[rgba(40,170,222,0.4)] text-foreground placeholder:text-foreground/40 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="industry" render={() => <FormItem>
            <div className="mb-4">
              <FormLabel className="text-foreground font-default font-semibold">Target Industry Verticals</FormLabel>
              <p className="text-[12px] text-foreground/60 mt-1 font-default">Your data is secured with enterprise-grade encryption and privacy protocols.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {industryOptions.map(item => <FormField key={item} control={control} name="industry" render={({
          field
        }) => {
          return <FormItem key={item} className="flex flex-row items-start space-x-3 space-y-0 rounded-sm border border-primary/10 bg-[#F0FAFF]/50 p-3 hover:bg-[#F0FAFF] transition-colors">
                        <FormControl>
                          <input type="checkbox" className="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground accent-primary" checked={field.value?.includes(item)} onChange={e => {
                const checked = e.target.checked;
                return checked ? field.onChange([...(field.value || []), item]) : field.onChange(field.value?.filter((value: string) => value !== item));
              }} />
                        </FormControl>
                        <FormLabel className="font-default font-normal cursor-pointer text-foreground peer-checked:text-primary transition-colors">
                          {item}
                        </FormLabel>
                      </FormItem>;
        }} />)}
            </div>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="audit_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default font-semibold">Audit Project Scope</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[120px] w-full rounded-sm border border-primary/40 bg-[#F0FAFF] px-3 py-2 text-base shadow-sm placeholder:text-foreground/40 focus-visible:outline-none focus-visible:ring-[4px] focus-visible:ring-[rgba(40,170,222,0.4)] focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-foreground resize-y font-default" placeholder="Tell us about your current challenges, existing tech stack, and specific AI objectives." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-sm bg-destructive/10 border border-destructive/20 text-destructive text-sm font-default">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-lg font-default font-semibold bg-primary text-primary-foreground hover:bg-secondary shadow-md transition-all rounded-sm">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Analyzing Scope...
          </> : "Request Your Custom AI Audit"}
      </Button>
    </Form>;
}